import { useId, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { humanise } from '@/lib/format';

/**
 * The controls a curation form is built from.
 *
 * Lifted out of `ExerciseFields` when the food catalog grew a create page: two
 * forms over closed vocabularies, nullable enums and "nobody has looked" blanks
 * want the same three controls, and two copies of `NullableSelect` is how the
 * unassessed option ends up meaning different things on different screens.
 */

/**
 * The selects on these forms stay native, so this mirrors `Input`'s surface by
 * hand rather than reaching for the console's `Select`.
 *
 * Radix has no concept of an empty option — `value=""` clears the listbox
 * rather than selecting something — and the whole point of `NullableSelect`
 * below is that "nobody has assessed this" is a real, re-selectable answer. A
 * sentinel value would make the unassessed state a translation layer over a
 * control that cannot hold it.
 */
export const NATIVE_SELECT =
  'flex h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 text-sm shadow-xs outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60';

/**
 * The field caption. Uppercase and tracked out so a form of twenty controls
 * reads as labelled rows rather than as twenty sentences.
 */
export const MICRO_LABEL =
  'text-xs leading-none font-medium tracking-[0.04em] text-muted-foreground uppercase';

/** The empty option's value. A real empty string, so the select can hold it. */
const UNASSESSED = '';

interface LabelledProps {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}

export function Labelled({ id, label, hint, children }: LabelledProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className={MICRO_LABEL}>
        {label}
      </Label>
      {children}
      {hint === undefined ? null : <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

interface SelectFieldProps<T extends string> {
  readonly label: string;
  readonly hint?: string;
  readonly options: readonly T[];
  readonly value: T;
  readonly disabled: boolean;
  readonly onChange: (next: T) => void;
}

/** A select over a closed vocabulary where every row has an answer. */
export function SelectField<T extends string>({
  label,
  hint,
  options,
  value,
  disabled,
  onChange,
}: SelectFieldProps<T>) {
  const id = useId();

  return (
    <Labelled id={id} label={label} {...(hint === undefined ? {} : { hint })}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value as T);
        }}
        className={NATIVE_SELECT}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {humanise(option)}
          </option>
        ))}
      </select>
    </Labelled>
  );
}

interface NullableSelectProps<T extends string> {
  readonly label: string;
  readonly hint?: string;
  readonly options: readonly T[];
  readonly value: T | null;
  readonly disabled: boolean;
  readonly onChange: (next: T | null) => void;
  /** What the empty option is called, when "unassessed" is not the right word. */
  readonly emptyLabel?: string;
}

/**
 * A select whose empty option is a value, not a prompt.
 *
 * Several curated fields are nullable and null means "nobody has assessed
 * this". A checkbox cannot say that, and a select that only offered answers
 * would let a curator turn an honest blank into a guess by tabbing past it —
 * so the unassessed option is spelled out, sits first, and is selectable again
 * after an answer has been given.
 */
export function NullableSelect<T extends string>({
  label,
  hint,
  options,
  value,
  disabled,
  onChange,
  emptyLabel = '— unassessed —',
}: NullableSelectProps<T>) {
  const id = useId();

  return (
    <Labelled id={id} label={label} {...(hint === undefined ? {} : { hint })}>
      <select
        id={id}
        value={value ?? UNASSESSED}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value === UNASSESSED ? null : (event.target.value as T));
        }}
        className={NATIVE_SELECT}
      >
        <option value={UNASSESSED}>{emptyLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {humanise(option)}
          </option>
        ))}
      </select>
    </Labelled>
  );
}

interface TokenPickerProps<T extends string> {
  readonly label: string;
  readonly hint?: string;
  readonly options: readonly T[];
  readonly selected: readonly T[];
  readonly disabled: boolean;
  readonly onToggle: (value: T) => void;
}

/**
 * A group of toggles rather than a multi-select: an operator picking three of
 * eighteen options should be able to see what they picked without scrolling a
 * list box, and `aria-pressed` says the state out loud.
 */
export function TokenPicker<T extends string>({
  label,
  hint,
  options,
  selected,
  disabled,
  onToggle,
}: TokenPickerProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <span className={MICRO_LABEL}>{label}</span>
      {hint === undefined ? null : <span className="text-xs text-muted-foreground">{hint}</span>}
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Button
            key={option}
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={selected.includes(option)}
            disabled={disabled}
            onClick={() => {
              onToggle(option);
            }}
            className={cn(
              'h-7 rounded-md px-2.5 text-xs font-normal text-muted-foreground',
              selected.includes(option) &&
                'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15 hover:text-primary',
            )}
          >
            {humanise(option)}
          </Button>
        ))}
      </div>
    </div>
  );
}
